import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../../../context/AuthContext";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  Stack,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import SummarizeIcon from "@mui/icons-material/Summarize";
import axios from "../../../config/axiosConfig";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import EquiposVariosAsignadosOT from "./EquiposVariosAsignadosOT";
import CentroEquipoDetalleDialog from "../../requerimiento/components/CentroEquipoDetalleDialog";

// Misma idea que AsignarEquiposParOT (checkbox por fila, paginado, compacto), pero
// para los servicios "otros" (todo menos TKR/AVL/Scada): Seguridad, POC, Energia,
// Ventas Varias, etc. El tipo de equipo es dinámico por servicio (tabla `equipo`),
// igual que el nav de pills de index.php armado con
// "Select e.nombre From equipo e, servicio s Where s.nombre=$tipo and s.id_servicio=e.id_servicio".
// Para POC, el Chip (SIM) se asigna acá igual que un equipo más (viene de
// equipo_chip en vez de equipo_varios); el emparejamiento con la Radio POC se
// hace después, en "Asignado" (ver EmparejarChipsPOC).
const PAGE_SIZE = 15;

const headCellSx = {
  fontWeight: 800,
  bgcolor: (t) => (t.palette.mode === "dark" ? t.palette.background.paper : t.palette.grey[100]),
};

// Input nativo (Box component="input") en vez de TextField para las celdas de la
// tabla de "Inventario Disponible" — con hasta cientos de filas por página, el
// armazon interno de TextField (FormControl + InputLabel + notched outline en SVG)
// por cada celda pesa mucho mas que un <input> con estilo propio, y acá no se usa
// label flotante de todos modos.
const inputCompactoSx = {
  fontSize: 10.5,
  fontFamily: "inherit",
  padding: "4px 6px",
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 0,
  outline: "none",
  bgcolor: "background.paper",
  color: "text.primary",
  "&:focus": { borderColor: "primary.main" },
  "&:disabled": { bgcolor: (t) => t.palette.action.disabledBackground, color: "text.disabled" },
};

// Mismo patrón que en AsignarEquiposParOT.jsx (TKR): ícono de color + borde
// superior de acento, para distinguir de un vistazo las 3 tarjetas.
const SectionCard = ({ title, icon, color = "primary", extra, children }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 1.25,
      borderRadius: 1,
      borderTop: "3px solid",
      borderTopColor: `${color}.main`,
    }}
  >
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
      <Box
        sx={{
          width: 26,
          height: 26,
          borderRadius: "50%",
          flexShrink: 0,
          display: "grid",
          placeItems: "center",
          bgcolor: (theme) => alpha(theme.palette[color].main, theme.palette.mode === "dark" ? 0.28 : 0.12),
          color: `${color}.main`,
        }}
      >
        {icon}
      </Box>
      <Typography variant="subtitle2" fontWeight={800}>{title}</Typography>
      {extra}
    </Stack>
    {children}
  </Paper>
);

const AsignarEquiposVariosOT = ({ cod_ot, cod_cliente, cod_servicio_cliente, servicio, onEquiposActualizados, onAsignadosCambiados }) => {
  const { user } = useAuth();
  const [tipos, setTipos] = useState([]);
  const [tiposLoading, setTiposLoading] = useState(true);
  const [tipo, setTipo] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [buscar, setBuscar] = useState("");
  const [buscarAplicado, setBuscarAplicado] = useState("");
  const [loading, setLoading] = useState(false);
  const [seleccion, setSeleccion] = useState({}); // { [id]: { grupo, id_ref, placa, observacion } }
  const [guardando, setGuardando] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [asignados, setAsignados] = useState([]);
  const [equipoDialog, setEquipoDialog] = useState(null);
  const equiposAsignadosRef = useRef();

  useEffect(() => {
    onAsignadosCambiados?.(asignados.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asignados]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const esChip = tipo === "Chip";
  const esTodos = tipo === "Todos";
  // "Chip" (POC) sale de otra tabla (equipo_chip) con columnas distintas — "Todos"
  // solo junta los tipos que sí viven en equipo_varios, Chip queda como pestaña aparte.
  const tiposVarios = tipos.filter((t) => t !== "Chip");

  useEffect(() => {
    setTiposLoading(true);
    axios
      .get("/api/equipos-varios/tipos", { params: { servicio } })
      .then((res) => {
        const lista = res.data || [];
        // POC usa chips (SIM) de la misma tabla equipo_chip que AVL, pero no está
        // registrado como tipo en la tabla `equipo`, así que se agrega a mano.
        const conChip = servicio === "POC" ? [...lista, "Chip"] : lista;
        setTipos(conChip);
        setTipo(conChip[0] || "");
      })
      .catch((err) => console.error("Error al cargar tipos de equipo", err))
      .finally(() => setTiposLoading(false));
  }, [servicio]);

  const cargar = (nextPage = page, buscarTexto = buscarAplicado) => {
    if (!tipo) return;
    setLoading(true);

    const peticion = esChip
      ? axios.get("/api/equipos-chip", {
          params: { estado: "Inactivo", ciudad: user.sucursal, buscar: buscarTexto, page: nextPage, pageSize: PAGE_SIZE },
        })
      : axios.get("/api/equipos-varios", {
          params: {
            tipo: esTodos ? tiposVarios.join(",") : tipo,
            estado: "Inactivo", ciudad: user.sucursal, buscar: buscarTexto, page: nextPage, pageSize: PAGE_SIZE,
          },
        });

    peticion
      .then((res) => {
        if (esChip) {
          // /api/equipos-chip responde { success, data: { rows, total } }, pero el
          // interceptor de axios ya aplana "data" en response.data (ver axiosConfig.js).
          const { rows = [], total: totalChips = 0 } = res.data || {};
          setItems(rows.map((c) => ({ ...c, id: c.ID, serie: c.numero })));
          setTotal(Number(totalChips));
          setPage(nextPage);
        } else {
          const data = res.data || {};
          setItems((data.items || []).map((e) => ({ ...e, id: e.ID })));
          setTotal(Number(data.total || 0));
          setPage(Number(data.page || nextPage));
        }
      })
      .catch((err) => console.error("Error al cargar equipos", err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!tipo) return;
    setSeleccion({});
    setBuscar("");
    setBuscarAplicado("");
    cargar(1, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipo, user.sucursal]);

  const seleccionados = Object.keys(seleccion);

  // Guarda la serie junto con la selección (no solo el id) para que sobreviva a
  // que el usuario busque otra cosa o cambie de página antes de "Adicionar".
  const toggle = (row) => {
    setSeleccion((prev) => {
      if (prev[row.id]) {
        const { [row.id]: _quitado, ...resto } = prev;
        return resto;
      }
      return { ...prev, [row.id]: { serie: row.serie, tipo: row.tipo, grupo: "", id_ref: "", placa: "", observacion: "" } };
    });
  };

  const setCampo = (id, campo, valor) => {
    setSeleccion((prev) => ({ ...prev, [id]: { ...prev[id], [campo]: valor } }));
  };

  const handleBuscar = () => {
    setBuscarAplicado(buscar.trim());
    cargar(1, buscar.trim());
  };

  const adicionar = async () => {
    if (!seleccionados.length) return;
    setGuardando(true);
    try {
      for (const idStr of seleccionados) {
        const id = Number(idStr);
        const datos = seleccion[idStr];
        if (!datos?.serie) continue;

        if (esChip) {
          // equipo_chip no tiene grupo/id_ref/placa, solo relación (se define en
          // Emparejar, ya en "Asignado") y observación.
          await axios.put(`/api/equipos-chip/${id}`, {
            estado: "Asignado",
            cod_servicio: cod_servicio_cliente,
            asignado: cod_cliente,
          });
          await axios.post(`/api/historial-chip/`, {
            numero: datos.serie,
            cod_ot,
            asignado: cod_cliente,
            ciudad: user.sucursal,
            estado: "Asignado",
            operacion: "Adicion",
            observacion: datos.observacion || "",
            usu: user.username,
          });
          continue;
        }

        const cambios = {
          estado: "Asignado",
          cod_servicio: cod_servicio_cliente,
          asignado: cod_cliente,
          grupo: datos.grupo || "",
          id_ref: datos.id_ref || "",
          placa: datos.placa || "",
        };
        // Igual que guarda.php: si el tipo de equipo es "Baterias", además se marca tipo_equipo='2'.
        // Se usa datos.tipo (el tipo real de la fila) y no el estado "tipo" de la
        // pestaña, porque con "Todos" seleccionado la selección puede mezclar varios tipos.
        if (datos.tipo === "Baterias") cambios.tipo_equipo = "2";

        await axios.put(`/api/equipos-varios/${id}`, cambios);
        await axios.post(`/api/historial-varios/`, {
          serie: datos.serie,
          cod_ot,
          propiedad: "TRS",
          asignado: cod_cliente,
          ciudad: user.sucursal,
          estado: "Asignado",
          operacion: "Adicion",
          observacion: datos.observacion || "",
          usu: user.username,
        });
      }

      setSeleccion({});
      setOpenSnackbar(true);
      cargar(page, buscarAplicado);
      equiposAsignadosRef.current?.refetch();
      if (onEquiposActualizados) onEquiposActualizados();
    } catch (err) {
      console.error("Error al asignar equipos:", err);
    } finally {
      setGuardando(false);
    }
  };

  if (tiposLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (!tipos.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic", mt: 1 }}>
        El servicio "{servicio}" no tiene tipos de equipo registrados.
      </Typography>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Grid container spacing={1.5}>
        {/* ── Columna izquierda: inventario disponible ── */}
        <Grid size={{ xs: 12, md: 8 }}>
          <SectionCard title="Inventario Disponible" icon={<Inventory2Icon fontSize="small" />} color="primary">
            <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
              <ToggleButtonGroup
                size="small"
                exclusive
                color="primary"
                value={tipo}
                onChange={(e, nuevo) => nuevo && setTipo(nuevo)}
                sx={{ flexWrap: "wrap", justifyContent: "center", rowGap: 0.5 }}
              >
                {tiposVarios.length > 1 && (
                  <ToggleButton value="Todos" sx={{ fontSize: 11.5, px: 1.5, py: 0.35, textTransform: "none", fontWeight: 700 }}>
                    Todos
                  </ToggleButton>
                )}
                {tipos.map((t) => (
                  <ToggleButton key={t} value={t} sx={{ fontSize: 11.5, px: 1.5, py: 0.35, textTransform: "none" }}>
                    {t}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>

            <Stack
              direction="row"
              spacing={1}
              useFlexGap
              flexWrap="wrap"
              alignItems="center"
              sx={{ mb: 1, rowGap: 1 }}
            >
              <TextField
                size="small"
                label="Buscar"
                value={buscar}
                onChange={(e) => setBuscar(e.target.value)}
                onKeyDown={(e) => {
                  // Enter simple inserta salto de línea (para poder pegar/escribir
                  // varios identificadores); Ctrl/Cmd+Enter dispara la búsqueda —
                  // mismo criterio que VenderEquipoOT.jsx / ListadoEquipos.jsx.
                  if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) handleBuscar();
                }}
                placeholder="Serie, EMAI... (pega una columna de Excel)"
                multiline
                minRows={1}
                maxRows={4}
                sx={{
                  minWidth: 220,
                  "& .MuiInputLabel-root": { fontSize: 11 },
                  "& .MuiOutlinedInput-root": { minHeight: 30, fontSize: 11.5, alignItems: "flex-start" },
                }}
              />
              <IconButton size="small" onClick={handleBuscar} title="Buscar">
                <SearchIcon fontSize="small" />
              </IconButton>
              <Chip size="small" label={`Total: ${total}`} sx={{ height: 20, fontSize: 11 }} />
              <Chip size="small" color="warning" variant="outlined" label={`Seleccionados: ${seleccionados.length}`} sx={{ height: 20, fontSize: 11 }} />
              <Box sx={{ flexGrow: 1 }} />
              <Button
                size="small"
                variant="contained"
                color="success"
                disabled={!seleccionados.length || guardando}
                onClick={adicionar}
                sx={{ minHeight: 26, height: 26, fontSize: 11, whiteSpace: "nowrap", px: 1.5 }}
              >
                {guardando ? <CircularProgress size={16} color="inherit" /> : "Adicionar Seleccionados"}
              </Button>
            </Stack>

            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 0.5 }}>
              <Table
                size="small"
                sx={{
                  tableLayout: "fixed",
                  "& .MuiTableCell-root": { py: 0.15, px: 0.6, fontSize: 10.5, lineHeight: 1.3 },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...headCellSx, width: 32 }}>Sel.</TableCell>
                    <TableCell sx={{ ...headCellSx, width: 130 }}>{esChip ? "Número" : "Serie"}</TableCell>
                    <TableCell sx={{ ...headCellSx, width: 130 }}>Modelo</TableCell>
                    {esTodos && <TableCell sx={{ ...headCellSx, width: 110 }}>Tipo</TableCell>}
                    {!esChip && (
                      <>
                        <TableCell sx={{ ...headCellSx, width: 70 }}>Grupo</TableCell>
                        <TableCell sx={{ ...headCellSx, width: 70 }}>ID Cliente</TableCell>
                        <TableCell sx={{ ...headCellSx, width: 70 }}>Placa</TableCell>
                      </>
                    )}
                    <TableCell sx={headCellSx}>Observación</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={esChip ? 4 : 7 + (esTodos ? 1 : 0)} align="center" sx={{ py: 2 }}>
                        <CircularProgress size={18} />
                      </TableCell>
                    </TableRow>
                  ) : !items.length ? (
                    <TableRow>
                      <TableCell colSpan={esChip ? 4 : 7 + (esTodos ? 1 : 0)}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                          {esTodos
                            ? `No hay equipos inactivos en ${user.sucursal || "tu sucursal"}.`
                            : `No hay equipos "${tipo}" inactivos en ${user.sucursal || "tu sucursal"}.`}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    items.map((row) => {
                      const checked = Boolean(seleccion[row.id]);
                      return (
                        <TableRow key={row.id} hover selected={checked}>
                          <TableCell>
                            <Checkbox size="small" checked={checked} onChange={() => toggle(row)} sx={{ p: 0 }} />
                          </TableCell>
                          <TableCell>
                            <Box
                              component="span"
                              onClick={() => setEquipoDialog({ tipo: esChip ? "chip" : "varios", identificador: row.serie })}
                              sx={{ cursor: "pointer", color: "primary.main", textDecoration: "underline" }}
                            >
                              {row.serie}
                            </Box>
                          </TableCell>
                          <TableCell>{row.modelo}</TableCell>
                          {esTodos && <TableCell>{row.tipo}</TableCell>}
                          {!esChip && (
                            <>
                              <TableCell>
                                <Box
                                  component="input"
                                  disabled={!checked}
                                  value={seleccion[row.id]?.grupo || ""}
                                  onChange={(e) => setCampo(row.id, "grupo", e.target.value)}
                                  sx={{ ...inputCompactoSx, width: 60 }}
                                />
                              </TableCell>
                              <TableCell>
                                <Box
                                  component="input"
                                  disabled={!checked}
                                  value={seleccion[row.id]?.id_ref || ""}
                                  onChange={(e) => setCampo(row.id, "id_ref", e.target.value)}
                                  sx={{ ...inputCompactoSx, width: 60 }}
                                />
                              </TableCell>
                              <TableCell>
                                <Box
                                  component="input"
                                  disabled={!checked}
                                  value={seleccion[row.id]?.placa || ""}
                                  onChange={(e) => setCampo(row.id, "placa", e.target.value)}
                                  sx={{ ...inputCompactoSx, width: 60 }}
                                />
                              </TableCell>
                            </>
                          )}
                          <TableCell>
                            <Box
                              component="input"
                              disabled={!checked}
                              value={seleccion[row.id]?.observacion || ""}
                              onChange={(e) => setCampo(row.id, "observacion", e.target.value)}
                              sx={{ ...inputCompactoSx, width: "100%" }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center" sx={{ mt: 0.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                Página {total ? page : 0} / {total ? totalPages : 0}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={<NavigateBeforeIcon />}
                disabled={loading || page <= 1}
                onClick={() => cargar(page - 1)}
                sx={{ minHeight: 26, height: 26, fontSize: 11 }}
              >
                Anterior
              </Button>
              <Button
                size="small"
                variant="outlined"
                endIcon={<NavigateNextIcon />}
                disabled={loading || page >= totalPages}
                onClick={() => cargar(page + 1)}
                sx={{ minHeight: 26, height: 26, fontSize: 11 }}
              >
                Siguiente
              </Button>
            </Stack>
          </SectionCard>
        </Grid>

        {/* ── Columna derecha: equipos ya asignados a la OT + resumen ── */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Stack spacing={1.5}>
            <SectionCard
              title="Equipos en la Orden"
              icon={<AssignmentTurnedInIcon fontSize="small" />}
              color="success"
              extra={
                <Chip
                  size="small"
                  color="success"
                  label={asignados.length}
                  sx={{ height: 20, fontSize: 11, fontWeight: 700 }}
                />
              }
            >
              <EquiposVariosAsignadosOT ref={equiposAsignadosRef} cod_ot={cod_ot} servicio={servicio} onData={setAsignados} />
            </SectionCard>

            <SectionCard title="Resumen de Orden" icon={<SummarizeIcon fontSize="small" />} color="secondary">
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" color="text.secondary">Total Equipos</Typography>
                <Typography variant="body2" fontWeight={700}>{asignados.length}</Typography>
              </Box>
            </SectionCard>
          </Stack>
        </Grid>
      </Grid>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <MuiAlert onClose={() => setOpenSnackbar(false)} severity="success" variant="filled" sx={{ width: "100%" }}>
          Equipos asignados correctamente.
        </MuiAlert>
      </Snackbar>

      <CentroEquipoDetalleDialog
        open={!!equipoDialog}
        tipo={equipoDialog?.tipo}
        identificador={equipoDialog?.identificador}
        onClose={() => setEquipoDialog(null)}
      />
    </Box>
  );
};

export default AsignarEquiposVariosOT;
